<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  list: {
    type: Object,
    default: null,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const isSubmitting = ref(false);

const form = ref({
  name: '',
  description: '',
});

const dialogTitle = computed(() =>
  props.isEditing
    ? t('CONTACT_LISTS.EDIT.TITLE')
    : t('CONTACT_LISTS.CREATE.TITLE')
);

const isFormValid = computed(() => {
  return form.value.name?.trim();
});

watch(
  () => props.list,
  newList => {
    if (newList) {
      form.value = {
        name: newList.name || '',
        description: newList.description || '',
      };
    }
  },
  { immediate: true }
);

onMounted(() => {
  dialogRef.value?.open();
});

const handleClose = () => {
  dialogRef.value?.close();
  emit('close');
};

const handleSubmit = async () => {
  if (!isFormValid.value) return;

  isSubmitting.value = true;

  try {
    if (props.isEditing) {
      await store.dispatch('contactLists/update', {
        id: props.list.id,
        ...form.value,
      });
      useAlert(t('CONTACT_LISTS.API.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('contactLists/create', form.value);
      useAlert(t('CONTACT_LISTS.API.CREATE_SUCCESS'));
    }
    handleClose();
  } catch (error) {
    useAlert(t('CONTACT_LISTS.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="dialogTitle"
    size="medium"
    @close="handleClose"
  >
    <form class="space-y-4" @submit.prevent="handleSubmit">
      <Input
        v-model="form.name"
        :label="t('CONTACT_LISTS.FORM.NAME.LABEL')"
        :placeholder="t('CONTACT_LISTS.FORM.NAME.PLACEHOLDER')"
      />

      <div>
        <label class="mb-1 text-sm font-medium text-n-slate-12 block">
          {{ t('CONTACT_LISTS.FORM.DESCRIPTION.LABEL') }}
        </label>
        <TextArea
          v-model="form.description"
          :placeholder="t('CONTACT_LISTS.FORM.DESCRIPTION.PLACEHOLDER')"
          min-height="5rem"
          max-height="10rem"
        />
      </div>
    </form>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          variant="faded"
          color="slate"
          type="button"
          :label="t('CONTACT_LISTS.FORM.CANCEL')"
          class="bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
          @click="handleClose"
        />
        <Button
          :label="
            isEditing
              ? t('CONTACT_LISTS.FORM.UPDATE')
              : t('CONTACT_LISTS.FORM.CREATE')
          "
          :is-loading="isSubmitting"
          :disabled="!isFormValid || isSubmitting"
          @click="handleSubmit"
        />
      </div>
    </template>
  </Dialog>
</template>
